<template>
  <div
    class="langSelector"
    :class="[ showBlock ? '-open' : '', fromMenu ? `-${fromMenu}` : '' ]"
  >
    <div
      class="langSelector_trigger"
      @click="onToggleBlock"
    >
      <div class="langSelector_iconPlaceholder">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
         <path d="M2 12C2 17.523 6.477 22 12 22C17.523 22 22 17.523 22 12C22 6.477 17.523 2 12 2C6.477 2 2 6.477 2 12Z" stroke="#A990E2" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
         <path d="M12.9605 2C12.9605 2 15.842 5.96985 15.842 12C15.842 18.0301 12.9605 22 12.9605 22M11.0395 22C11.0395 22 8.15795 18.0301 8.15795 12C8.15795 5.96985 11.0395 2 11.0395 2M3 15.5176H21M3 8.48241H21" stroke="#A990E2" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>

      </div>
      <span class="langSelector_selected">{{ selectedLang }}</span>
      <div class="langSelector_chevron">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g clip-path="url(#clip0_21390_13964)">
            <g clip-path="url(#clip1_21390_13964)">
              <path d="M3.5 7L10 13L16.5 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </g>
          </g>
          <defs>
            <clipPath id="clip0_21390_13964"><rect width="20" height="20" fill="white"/></clipPath>
            <clipPath id="clip1_21390_13964"><rect width="20" height="20" fill="white"/></clipPath>
          </defs>
        </svg>
      </div>
    </div>

    <transition name="langSelector-dropdown">
      <div
        v-if="showBlock"
        class="langSelector_dropdown"
      >
        <div
          v-for="(item, key) in otherLanguages"
          :key="key"
          class="langSelector_item"
          @click="setLang(key)"
        >
          <div class="langSelector_flagPlaceholder">
            <i :class="`langSelector_flagIcon -${key}`" />
          </div>
          <span class="langSelector_itemText">{{ item }}</span>
        </div>
      </div>
    </transition>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, PropType } from 'vue'
import useLang from '@web/composition/useLang'
import { availableLang } from '@web/common/locale'

defineOptions({
  name: 'LangSelector'
})

defineProps({
  fromMenu: {
    type: String as PropType<'navBarMobile' | 'settingsSidebar'>,
    default: ''
  }
})

const showBlock = ref(false)

function onToggleBlock () {
  showBlock.value = !showBlock.value
}

const { selectedLang, setLang, lang } = useLang()

const otherLanguages = computed(() => {
  return Object.fromEntries(
    Object.entries(availableLang).filter(([key]) => key !== lang.value)
  )
})
</script>

<style lang="scss" scoped>
@import '@web/assets/scss/textContents.scss';

.langSelector {
  width: 100%;
  user-select: none;
  position: relative;
  overflow: hidden;

  &_trigger {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px;
    background-color: var(--ac-color-white);
    border-radius: 12px;
    cursor: pointer;
    color: var(--ac-color-dark-gray);
    @extend .text-sm;
    overflow: hidden;
  }

  &.-navBarMobile &_trigger {
    padding: 0;
    padding-right: 22px;
    border-radius: 6px;
  }

  &.-settingsSidebar &_trigger {
    padding: 12px;
  }

  &.-open &_trigger {
    background-color: var(--ac-color-liliac-secondary);
    color: var(--ac-color-deep-liliac);
  }

  &_iconPlaceholder {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    border-radius: 6px;
    background-color: transparent;
    overflow: hidden;
  }

  &.-navBarMobile &_iconPlaceholder {
    width: 40px;
    height: 40px;
    padding: 10px;

    svg {
      width: 20px;
      height: 20px;
    }
  }

  &.-navBarMobile.-open &_iconPlaceholder {
    background-color: var(--ac-color-deep-liliac);

    svg path{
      stroke: var(--ac-color-white);
    }
  }

  &.-settingsSidebar &_trigger {
    padding: 12px;
  }

  &_selected {
    flex: 1;
  }

  &_chevron {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    transform: rotate(0deg);
    transition: transform 0.2s;

    .-open & {
      transform: rotate(180deg);
    }
  }

  &_dropdown {
    margin-top: 4px;
    background-color: var(--ac-color-white);
    border-radius: 8px;
  }

  &_item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px;
    border-radius: 6px;
    cursor: pointer;
    @extend .text-sm;
    color: var(--ac-color-dark-gray);

    &:hover {
      background-color: #F8F8F9;
      color: var(--ac-color-black);
    }
  }

  &.-navBarMobile &_item {
    padding: 0px;
  }

  &_flagPlaceholder {
    width: 24px;
    height: 18px;
    flex-shrink: 0;
  }

  &.-navBarMobile &_flagPlaceholder {
    width: 40px;
    height: 40px;
    padding: 11px 8px;

    svg {
      width: 20px;
      height: 20px;
    }
  }

  &_flagIcon {
    display: flex;
    width: 24px;
    height: 18px;
    flex-shrink: 0;

    &.-en {
      background-image: url(~@web/assets/images/flags/en.svg);
    }

    &.-es {
      background-image: url(~@web/assets/images/flags/es.svg);
    }

    &.-ru {
      background-image: url(~@web/assets/images/flags/ru.svg);
    }
  }

  &_itemText {
    flex: 1;
  }
}

.langSelector-dropdown-enter-active,
.langSelector-dropdown-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.langSelector-dropdown-enter-from,
.langSelector-dropdown-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
